/**
 * 1895 Splash Loader — cinematic reveal for all loading states.
 * 
 * Props:
 *   - message: optional text below the logo
 *   - fullScreen: true = min-h-screen centered, false = inline flex
 *   - compact: true = smaller logo, quicker animation (for inline loaders)
 */
import { useState, useEffect } from 'react';

export default function SplashLoader({ message, fullScreen = true, compact = false }) {
  const [phase, setPhase] = useState(0);
  const w = compact ? 160 : 240;
  const h = w * (200 / 560);

  // Faster sequence for loaders (total ~1.8s)
  useEffect(() => {
    const delays = compact
      ? [100, 300, 500, 700, 900, 1100]
      : [200, 500, 800, 1100, 1400, 1700];
    const timers = delays.map((d, i) => setTimeout(() => setPhase(i + 1), d));
    return () => timers.forEach(clearTimeout);
  }, [compact]);

  const wrapper = fullScreen
    ? 'min-h-screen bg-[#09090b] flex flex-col items-center justify-center'
    : 'flex flex-col items-center justify-center py-12';

  return (
    <div className={wrapper} data-testid="splash-loader">
      <svg viewBox="0 0 560 200" xmlns="http://www.w3.org/2000/svg" width={w} height={h}>
        <defs>
          <radialGradient id="sGlow1" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#DAA520" stopOpacity="0.4" />
            <stop offset="60%" stopColor="#DAA520" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#DAA520" stopOpacity="0" />
          </radialGradient>
          <radialGradient id="sGlow2" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#FFD700" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#FFD700" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Projector glow */}
        <circle cx="258" cy="95" r="100" fill="url(#sGlow2)" style={{
          opacity: phase >= 1 ? 1 : 0,
          transition: 'opacity 0.6s ease-in',
        }}>
          <animate attributeName="r" values="95;108;95" dur="3s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.6;1;0.6" dur="3s" repeatCount="indefinite" />
        </circle>

        <circle cx="258" cy="95" r="65" fill="url(#sGlow1)" style={{
          opacity: phase >= 1 ? 1 : 0,
          transition: 'opacity 0.5s ease-in',
        }}>
          <animate attributeName="r" values="60;70;60" dur="3s" repeatCount="indefinite" />
        </circle>

        {/* "8" — the light source */}
        <text x="218" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" letterSpacing="8"
          style={{ fill: '#DAA520', opacity: phase >= 2 ? 1 : 0, transition: 'opacity 0.5s ease-out' }}>
          8
          <animate attributeName="fill" values="#DAA520;#FFD700;#DAA520" dur="3s" repeatCount="indefinite" />
        </text>

        {/* "1" */}
        <text x="158" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" letterSpacing="8"
          style={{ fill: '#F5E6C8', opacity: phase >= 3 ? 1 : 0, transition: 'opacity 0.4s ease-out' }}>1</text>

        {/* "95" */}
        <text x="282" y="122" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="95" letterSpacing="8"
          style={{ fill: '#F5E6C8', opacity: phase >= 3 ? 1 : 0, transition: 'opacity 0.4s ease-out' }}>95</text>

        {/* Film perforations */}
        {[65, 80, 95, 110, 125].map((y, i) => (
          <g key={i} style={{ opacity: phase >= 4 ? 1 : 0, transition: `opacity 0.4s ease-out ${i * 0.06}s` }}>
            <rect x="110" y={y - 3} width="9" height="6" rx="1" fill="none" stroke="#333" strokeWidth="0.5">
              <animate attributeName="opacity" values="0.5;0.8;0.5" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            </rect>
            <rect x="441" y={y - 3} width="9" height="6" rx="1" fill="none" stroke="#333" strokeWidth="0.5">
              <animate attributeName="opacity" values="0.5;0.8;0.5" dur={`${2 + i * 0.3}s`} repeatCount="indefinite" />
            </rect>
          </g>
        ))}

        {/* Tagline */}
        <text x="280" y="168" textAnchor="middle" fontFamily="'Palatino Linotype','Book Antiqua',Palatino,serif" fontSize="12" letterSpacing="5" fontStyle="italic"
          style={{ fill: '#555', opacity: phase >= 5 ? 1 : 0, transition: 'opacity 0.8s ease-out' }}>the birth of cinema</text>

        {/* Scanning beam (only during early phases) */}
        {phase >= 1 && phase < 4 && (
          <rect x="130" y="0" width="300" height="1" fill="#DAA520" opacity="0.1">
            <animate attributeName="y" from="50" to="150" dur="1.5s" repeatCount="indefinite" />
          </rect>
        )}
      </svg>

      {/* Loading message */}
      {message && (
        <p className="mt-5 text-sm text-[#666] font-light tracking-wide" style={{
          opacity: phase >= 5 ? 1 : 0,
          transition: 'opacity 0.6s ease-out',
          fontFamily: "'Palatino Linotype','Book Antiqua',Palatino,serif",
        }}>{message}</p>
      )}
    </div>
  );
}
